import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import DataTable from "../../../components/common/DataTable";
import DataTableLayout from "../../../layouts/DataTableLayout";
import { Button, Modal, SelectDropdown, LoadingSkeleton } from "../../../components/ui";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import TowerForm from "../../../components/societies/TowerForm";
import { toast } from "react-toastify";
import styles from "./TowerManagement.module.css";
import { Plus, Trash2 } from "lucide-react";

import { deleteTower, getTowersBySociety, getSocieties } from "../../../services/societyService";

const TowerManagement = () => {
  const [societies, setSocieties] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSocietyId = searchParams.get("societyId") || "";
  const [towers, setTowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Fetch societies for the dropdown
  const fetchSocieties = useCallback(async () => {
    try {
      const data = await getSocieties();
      setSocieties(data || []);
      if ((!selectedSocietyId || selectedSocietyId === "null") && data && data.length > 0) {
        setSearchParams({ societyId: String(data[0].society_id) });
      }
    } catch (err) {
      console.error("Error fetching societies:", err);
    }
  }, [selectedSocietyId, setSearchParams]);

  const fetchTowers = useCallback(async () => {
    if (!selectedSocietyId) {
      setTowers([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getTowersBySociety(selectedSocietyId);
      setTowers(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedSocietyId]);

  useEffect(() => {
    fetchSocieties();
  }, [fetchSocieties]);

  useEffect(() => {
    if (selectedSocietyId) fetchTowers();
  }, [selectedSocietyId, fetchTowers]);

  const selectedSocietyName = useMemo(() => {
    const match = societies.find((s) => String(s.society_id) === String(selectedSocietyId));
    return match?.society_name || "";
  }, [societies, selectedSocietyId]);

  const columns = [
    {
      header: "Sr No",
      render: (_, i) => i + 1,
    },
    {
      header: "Tower Name",
      accessor: "tower_name",
    },
    {
      header: "Action",
      render: (row) => (
        <Button
          variant="danger"
          size="sm"
          title="Delete Tower"
          onClick={() =>
            setDeleteTarget({
              row,
              type: "Tower",
              id: row.tower_id,
              name: row.tower_name || `Tower #${row.tower_id}`,
              description:
                "Deleting this tower will also permanently remove all of its associated floors and units.",
              canDelete: true,
              actionLabel: "Delete",
            })
          }
          icon={<Trash2 size={14} />}
        />
      ),
    },
  ];

  return (
    <AdminLayout>
      <DataTableLayout
        title={`Tower Management${selectedSocietyName ? ` - ${selectedSocietyName}` : ""}`}
        filters={
          <div className={styles.societySelect}>
            <SelectDropdown
              value={selectedSocietyId}
              onChange={(e) => setSearchParams({ societyId: e.target.value })}
              placeholder="Select Property..."
              options={(societies || []).map((s) => ({
                label: s.society_name,
                value: String(s.society_id),
              }))}
              isClearable={false}
              isSearchable
            />
          </div>
        }
        actions={
          <Button
            onClick={() => setOpen(true)}
            disabled={!selectedSocietyId}
            icon={<Plus size={16} />}
          >
            Manage Towers
          </Button>
        }
      >
        {loading ? (
          <div className={styles.loading}>
            <LoadingSkeleton count={3} height="48px" />
          </div>
        ) : (
          <DataTable columns={columns} data={towers} />
        )}
      </DataTableLayout>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Manage Towers"
      >
        <TowerForm
          societyId={selectedSocietyId}
          towers={towers}
          onSuccess={() => {
            setOpen(false);
            fetchTowers();
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Tower"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        description={deleteTarget?.description}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Delete"}
        onConfirm={async () => {
          if (!deleteTarget?.row) return;
          try {
            await deleteTower(deleteTarget.row.tower_id);
            toast.success("Tower deleted successfully");
            fetchTowers();
          } catch (err) {
            console.error(err);
            toast.error(err?.message || "Failed to delete tower.");
          }
        }}
      />
    </AdminLayout>
  );
};

export default TowerManagement;
